import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  CircularProgress,
  Paper,
} from "@mui/material";
import {
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  Star as StarIcon,
  Pending as PendingIcon,
  Receipt as ReceiptIcon,
  AttachMoney as MoneyIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { api_urls } from "../../../utils/api-urls";

const MilanSathiDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const response = await fetch(`${api_urls}api/admins/dashboard/stats`);
      const data = await response.json();
      if (data.success) {
        setStats(data.data);
      }
    } catch (error) {
      console.error("Error fetching dashboard stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCardClick = (route) => {
    navigate(route);
  };

  const statCards = stats
    ? [
        {
          title: "Total Users",
          value: stats.totalUsers?.toLocaleString() || 0,
          icon: <PeopleIcon sx={{ fontSize: 40 }} />,
          color: "#1976d2",
          bgColor: "#e3f2fd",
          route: "/milan-sathi/users",
        },
        {
          title: "New Users (30 days)",
          value: stats.newUsers?.toLocaleString() || 0,
          icon: <PersonAddIcon sx={{ fontSize: 40 }} />,
          color: "#2e7d32",
          bgColor: "#e8f5e9",
          route: "/milan-sathi/users?filter=new",
        },
        {
          title: "Premium Users",
          value: stats.premiumUsers?.toLocaleString() || 0,
          icon: <StarIcon sx={{ fontSize: 40 }} />,
          color: "#ed6c02",
          bgColor: "#fff3e0",
          route: "/milan-sathi/users?filter=premium",
        },
        {
          title: "Pending Verifications",
          value: stats.pendingVerifications?.toLocaleString() || 0,
          icon: <PendingIcon sx={{ fontSize: 40 }} />,
          color: "#9c27b0",
          bgColor: "#f3e5f5",
          route: "/milan-sathi/verifications",
        },
        {
          title: "Total Transactions",
          value: stats.totalTransactions?.toLocaleString() || 0,
          icon: <ReceiptIcon sx={{ fontSize: 40 }} />,
          color: "#0288d1",
          bgColor: "#e1f5fe",
          route: "/milan-sathi/transactions",
        },
        {
          title: "Total Revenue",
          value: `₹${stats.totalRevenue?.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }) || "0.00"}`,
          icon: <MoneyIcon sx={{ fontSize: 40 }} />,
          color: "#388e3c",
          bgColor: "#e8f5e9",
          route: "/milan-sathi/transactions",
        },
      ]
    : [];

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: "bold" }}>
        Milan Sathi Dashboard
      </Typography>

      <Grid container spacing={3}>
        {statCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Card
              sx={{
                height: "100%",
                cursor: "pointer",
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: 6,
                  borderColor: card.color,
                  border: `2px solid ${card.color}`,
                },
              }}
              onClick={() => handleCardClick(card.route)}
            >
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="textSecondary" variant="body2" gutterBottom>
                      {card.title}
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: "bold", color: card.color }}>
                      {card.value}
                    </Typography>
                    <Typography variant="caption" color="primary" sx={{ mt: 1 }}>
                      Click to view details →
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      backgroundColor: card.bgColor,
                      borderRadius: "50%",
                      p: 1.5,
                      color: card.color,
                    }}
                  >
                    {card.icon}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ mt: 4, p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Quick Links
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                cursor: "pointer",
                "&:hover": { backgroundColor: "#f5f5f5" },
              }}
              onClick={() => navigate("/milan-sathi/users")}
            >
              <CardContent>
                <Typography variant="subtitle1">Manage Users</Typography>
                <Typography variant="body2" color="textSecondary">
                  View and manage all Milan Sathi users
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                cursor: "pointer",
                "&:hover": { backgroundColor: "#f5f5f5" },
              }}
              onClick={() => navigate("/milan-sathi/verifications")}
            >
              <CardContent>
                <Typography variant="subtitle1">Verifications</Typography>
                <Typography variant="body2" color="textSecondary">
                  Review pending verification requests
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                cursor: "pointer",
                "&:hover": { backgroundColor: "#f5f5f5" },
              }}
              onClick={() => navigate("/milan-sathi/plans")}
            >
              <CardContent>
                <Typography variant="subtitle1">Subscription Plans</Typography>
                <Typography variant="body2" color="textSecondary">
                  Manage subscription plans
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                cursor: "pointer",
                "&:hover": { backgroundColor: "#f5f5f5" },
              }}
              onClick={() => navigate("/milan-sathi/transactions")}
            >
              <CardContent>
                <Typography variant="subtitle1">Transactions</Typography>
                <Typography variant="body2" color="textSecondary">
                  View all payment transactions
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default MilanSathiDashboard;
